import type { AudioConfig } from '../config/audio.config';
import { SoundId, type SoundBackend } from './AudioManager';
import { MusicTrack } from './MusicTrack';

/**
 * Sound effects synthesized with the Web Audio API (no files to load), comic style like
 * mobile runners, plus the optional background music track (streamed, MusicTrack).
 *
 *  - Pickup: the classic two-note "pling" (bright FM bell, a fourth up), always the same pitch.
 *    Bonus items add a third note, the extra life plays a short major fanfare – all in A major.
 *  - Footsteps: soft low thumps with a little gravel crunch, alternating feet.
 *  - Jump "hup" (rising), double jump higher, landing thud, slide swoosh with a slide whistle.
 *  - Fall into a pit: falling whoosh, then a thud; head bump: a comic "bonk".
 *  - Extra-life drink: the bottle opens with a fizz, glug-glug gulps, a relieved "Ahh", a glass "kling".
 *  - Moving hazards (loudness by distance): reversing-alarm beeps (crane, excavator), a two-tone
 *    horn (forklift, truck), a short double beep before a lane change, a heavy impact thud, a creaking
 *    chain sling at the turning points of a swinging crane load.
 *  - Dozer: a low diesel chug per engine stroke (by distance), a scraping crunch when the blade meets a heap of
 *    earth, a long earth rumble with a thud when the pile fills the pit.
 *
 * The AudioContext starts suspended (mobile autoplay policy) and is resumed on the first gesture.
 */
const NOTE = { A5: 880, CS6: 1108.73, E6: 1318.51, A6: 1760, CS7: 2217.46 } as const;
const PLING = { first: 0.045, decay: 0.26, gain: 0.32, modRatio: 2, modIndex: 2.2 } as const;
const BONUS_GAP = 0.07;
const FANFARE = { notes: [NOTE.A5, NOTE.CS6, NOTE.E6, NOTE.A6], gap: 0.075, decay: 0.42 } as const;
/** "Ta-ta-ta – taaa": quick rising triad, then a held two-note chord an octave up. */
const LEVEL_UP = { run: [NOTE.A5, NOTE.CS6, NOTE.E6], gap: 0.085, runDecay: 0.3, chord: [NOTE.A6, NOTE.CS7], chordAt: 0.36, chordDecay: 0.8 } as const;
const STEP = { from: 120, to: 55, sweep: 0.07, decay: 0.11, gain: 0.55, crunch: 0.16, crunchCutoff: 900, crunchLength: 0.03, footDetune: 0.05 } as const;
const JUMP = { from: 260, to: 640, sweep: 0.11, decay: 0.14, gain: 0.22 } as const;
const DOUBLE_JUMP = { from: 420, to: 980, sweep: 0.1, decay: 0.16, gain: 0.2 } as const;
const LAND = { from: 150, to: 48, sweep: 0.1, decay: 0.16, gain: 0.7, dust: 0.22, dustCutoff: 700, dustLength: 0.07, fullImpact: 12 } as const;
const SLIDE = { from: 2400, to: 550, length: 0.34, gain: 0.3, q: 1.2, whistleFrom: 1150, whistleTo: 520, whistleGain: 0.06 } as const;
const FALL = { from: 1800, to: 240, length: 0.5, gain: 0.32, q: 0.9, thud: { from: 110, to: 38, sweep: 0.14, decay: 0.3, gain: 0.75 } } as const;
const BONK = { from: 760, to: 330, sweep: 0.08, decay: 0.13, gain: 0.3 } as const;
const ALARM = { frequency: 1046, beeps: 3, length: 0.13, gap: 0.1, gain: 0.11 } as const;
const SIGNAL = { frequency: 1397, beeps: 2, length: 0.06, gap: 0.07, gain: 0.08 } as const;
const HORN = { notes: [311, 392], length: 0.55, gain: 0.07, cutoff: 1600 } as const;
const IMPACT = { from: 95, to: 32, sweep: 0.2, decay: 0.42, gain: 0.85, rubble: 0.3, rubbleCutoff: 450, rubbleLength: 0.25 } as const;
/** Chain creak: a slightly falling sawtooth, roughened by a fast pitch wobble, through a narrow band – it swells in. */
const CREAK = { from: 520, to: 410, length: 0.38, attack: 0.05, gain: 0.08, wobbleHz: 34, wobbleDepth: 45, band: 1300, q: 6 } as const;
/** Bottle opened: a short pop and the fizz of escaping gas (high-passed noise). */
const BOTTLE_OPEN = { pop: { from: 900, to: 320, sweep: 0.03, decay: 0.05, gain: 0.25 }, fizzLength: 0.32, fizzCutoff: 3800, fizzGain: 0.14 } as const;
/** Gulp: a falling low "glug" followed by a small rising bubble. */
const GULP = { glug: { from: 240, to: 105, sweep: 0.07, decay: 0.1, gain: 0.55 }, bubble: { from: 480, to: 920, sweep: 0.05, decay: 0.06, gain: 0.1 }, bubbleAt: 0.06 } as const;
/** Relieved "Ahh": a falling voiced buzz through a vowel formant plus breath. */
const AHH = { from: 190, to: 135, length: 0.55, gain: 0.16, formant: 800, q: 3.5, breath: 0.05, breathCutoff: 1800 } as const;
/** Bau-Quiz: a rising "ding-ding-DING" when the question appears, a dry tick per second, a buzzer for wrong. */
const QUIZ_START = { notes: [NOTE.E6, NOTE.A6, NOTE.CS7], gap: 0.06, decay: 0.22 } as const;
const QUIZ_TICK = { frequency: 2093, decay: 0.035 } as const;
const QUIZ_WRONG = { from: 220, to: 110, length: 0.42, gain: 0.12, cutoff: 900 } as const;
/** Glass clink ("Prost!"): two high bells, slightly out of tune with each other. */
const CLINK = { notes: [3520, 4310], gap: 0.025, decay: 0.32 } as const;

/** Dozer: diesel chug (low knock + a puff of exhaust), earth crunch at the blade, the pile rumbling into the pit. */
const DOZER_CHUG = { from: 72, to: 44, sweep: 0.06, decay: 0.1, gain: 0.32, puff: 0.08, puffCutoff: 380, puffLength: 0.07 } as const;
const DOZER_PUSH = { from: 130, to: 52, sweep: 0.12, decay: 0.22, gain: 0.55, crunch: 0.34, crunchCutoff: 1400, crunchLength: 0.32 } as const;
const DOZER_FILL = { from: 85, to: 30, sweep: 0.3, decay: 0.55, gain: 0.8, rumble: 0.4, rumbleCutoff: 520, rumbleLength: 0.7 } as const;

/** Mini game hammer & nail: the clang's two partials (Hz) and how long it rings (s). */
const HAMMER_HIT = { high: 2960, low: 1870, decay: 0.42 } as const;

/** User gestures that may unlock audio (autoplay policy). */
const UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'click', 'keydown', 'touchend'] as const;

export class SynthSoundBackend implements SoundBackend {
  private context: AudioContext | null = null;
  private sfx: GainNode | null = null;
  private steps: GainNode | null = null;
  /** Streamed background music (null: no Web Audio in this browser). */
  private readonly music: MusicTrack | null;
  private readonly noise = new Map<number, AudioBuffer>();

  constructor(
    private readonly config: AudioConfig,
    musicEnabled = true,
    private effects = true,
  ) {
    // The context starts suspended until the first gesture; the music element starts buffering right away.
    const ctx = this.ensureContext();
    this.music = ctx ? new MusicTrack(ctx, config.music.candidates, config.volume.music, config.music.fadeInSeconds, musicEnabled) : null;
    // Synchronously inside the gesture – iOS ignores resume() / play() called later.
    const unlock = () => {
      const context = this.ensureContext();
      if (!context) return;
      if (context.state !== 'running') void context.resume();
      this.music?.play();
    };
    // Capture phase: UI buttons stop their pointer events from bubbling – the unlock must still see the tap.
    for (const type of UNLOCK_EVENTS) window.addEventListener(type, unlock, { capture: true });
  }

  /** Music on / off (effects keep playing); switching on is called from the toggle button's click. */
  setMusicEnabled(enabled: boolean): void {
    this.music?.setEnabled(enabled);
  }

  /** Game sounds on / off (the sound switch's "Ton: aus"); sounds already ringing fade out on their own. */
  setEffectsEnabled(enabled: boolean): void {
    this.effects = enabled;
  }

  play(id: SoundId, intensity: number): void {
    if (!this.config.enabled || !this.effects) return;
    const ctx = this.context;
    if (!ctx || ctx.state !== 'running') return;
    const t = ctx.currentTime;
    switch (id) {
      case SoundId.Collect:
        this.pling(ctx, t);
        if (intensity > 1) this.bell(ctx, NOTE.CS7, t + 2 * BONUS_GAP, PLING.decay);
        break;
      case SoundId.ExtraLife:
        FANFARE.notes.forEach((note, i) => this.bell(ctx, note, t + i * FANFARE.gap, FANFARE.decay));
        break;
      case SoundId.LevelUp:
        LEVEL_UP.run.forEach((note, i) => this.bell(ctx, note, t + i * LEVEL_UP.gap, LEVEL_UP.runDecay));
        for (const note of LEVEL_UP.chord) this.bell(ctx, note, t + LEVEL_UP.chordAt, LEVEL_UP.chordDecay);
        break;
      case SoundId.Footstep:
        this.thump(ctx, t, STEP, 1 + Math.sign(intensity) * STEP.footDetune, this.steps);
        this.noiseBurst(ctx, t, STEP.crunchLength, STEP.crunchCutoff, STEP.crunch, this.steps);
        break;
      case SoundId.Jump:
        this.sweep(ctx, t, JUMP);
        break;
      case SoundId.DoubleJump:
        this.sweep(ctx, t, DOUBLE_JUMP);
        break;
      case SoundId.Land: {
        const strength = Math.min(1, Math.max(0.35, intensity / LAND.fullImpact));
        this.thump(ctx, t, { ...LAND, gain: LAND.gain * strength }, 1, this.sfx);
        this.noiseBurst(ctx, t, LAND.dustLength, LAND.dustCutoff, LAND.dust * strength, this.sfx);
        break;
      }
      case SoundId.Slide:
        this.swoosh(ctx, t);
        break;
      case SoundId.Fall:
        this.whoosh(ctx, t, FALL);
        this.thump(ctx, t + FALL.length, FALL.thud, 1, this.sfx);
        break;
      case SoundId.HeadBump:
        this.sweep(ctx, t, BONK);
        break;
      case SoundId.HazardWarning:
        this.beeps(ctx, t, ALARM, intensity);
        break;
      case SoundId.VehicleSignal:
        this.beeps(ctx, t, SIGNAL, intensity);
        break;
      case SoundId.VehicleHorn:
        this.horn(ctx, t, intensity);
        break;
      case SoundId.BottleOpen:
        this.thump(ctx, t, BOTTLE_OPEN.pop, 1, this.sfx);
        this.hiss(ctx, t, BOTTLE_OPEN.fizzLength, BOTTLE_OPEN.fizzCutoff, BOTTLE_OPEN.fizzGain);
        break;
      case SoundId.Gulp:
        this.thump(ctx, t, GULP.glug, 1, this.sfx);
        this.sweep(ctx, t + GULP.bubbleAt, GULP.bubble);
        break;
      case SoundId.Ahh:
        this.ahh(ctx, t);
        break;
      case SoundId.QuizStart:
        QUIZ_START.notes.forEach((note, i) => this.bell(ctx, note, t + i * QUIZ_START.gap, QUIZ_START.decay));
        break;
      case SoundId.QuizTick:
        this.bell(ctx, QUIZ_TICK.frequency, t, QUIZ_TICK.decay);
        break;
      case SoundId.QuizRight:
        FANFARE.notes.forEach((note, i) => this.bell(ctx, note, t + i * FANFARE.gap, FANFARE.decay));
        break;
      case SoundId.QuizWrong:
        this.buzzer(ctx, t);
        break;
      case SoundId.Clink:
        CLINK.notes.forEach((note, i) => this.bell(ctx, note, t + i * CLINK.gap, CLINK.decay));
        break;
      case SoundId.HammerHit:
        // steel on steel: two inharmonic bell partials and a short knock
        this.bell(ctx, HAMMER_HIT.high, t, HAMMER_HIT.decay * (0.6 + 0.4 * intensity));
        this.bell(ctx, HAMMER_HIT.low, t, HAMMER_HIT.decay * 0.7);
        this.thump(ctx, t, { ...IMPACT, gain: IMPACT.gain * 0.55 * intensity }, 1.6, this.sfx);
        break;
      case SoundId.HazardImpact:
        this.thump(ctx, t, { ...IMPACT, gain: IMPACT.gain * intensity }, 1, this.sfx);
        this.noiseBurst(ctx, t, IMPACT.rubbleLength, IMPACT.rubbleCutoff, IMPACT.rubble * intensity, this.sfx);
        break;
      case SoundId.ChainCreak:
        this.creak(ctx, t, intensity);
        break;
      case SoundId.DozerEngine:
        this.thump(ctx, t, { ...DOZER_CHUG, gain: DOZER_CHUG.gain * intensity }, 1, this.sfx);
        this.noiseBurst(ctx, t, DOZER_CHUG.puffLength, DOZER_CHUG.puffCutoff, DOZER_CHUG.puff * intensity, this.sfx);
        break;
      case SoundId.DozerPush:
        this.thump(ctx, t, DOZER_PUSH, 1, this.sfx);
        this.noiseBurst(ctx, t, DOZER_PUSH.crunchLength, DOZER_PUSH.crunchCutoff, DOZER_PUSH.crunch, this.sfx);
        break;
      case SoundId.DozerFill:
        this.thump(ctx, t, DOZER_FILL, 1, this.sfx);
        this.noiseBurst(ctx, t, DOZER_FILL.rumbleLength, DOZER_FILL.rumbleCutoff, DOZER_FILL.rumble, this.sfx);
        break;
      default:
        break;
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.context) return this.context;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    const ctx = new Ctor();
    const limiter = ctx.createDynamicsCompressor(); // overlapping sounds never clip
    limiter.connect(ctx.destination);
    this.sfx = ctx.createGain();
    this.sfx.gain.value = this.config.volume.sfx;
    this.sfx.connect(limiter);
    this.steps = ctx.createGain();
    this.steps.gain.value = this.config.volume.footsteps;
    this.steps.connect(limiter);
    this.context = ctx;
    return ctx;
  }

  /** Classic pickup: two quick bell notes a fourth apart (E6 → A6), fixed pitch. */
  private pling(ctx: AudioContext, t: number): void {
    this.bell(ctx, NOTE.E6, t, PLING.first);
    this.bell(ctx, NOTE.A6, t + PLING.first, PLING.decay);
  }

  /** Bright FM bell (sine carrier, harmonic modulator with a decaying index). */
  private bell(ctx: AudioContext, frequency: number, t: number, decay: number): void {
    const out = this.sfx;
    if (!out) return;
    const carrier = ctx.createOscillator();
    carrier.frequency.value = frequency;
    const modulator = ctx.createOscillator();
    modulator.frequency.value = frequency * PLING.modRatio;
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(frequency * PLING.modIndex, t);
    depth.gain.exponentialRampToValueAtTime(frequency * 0.05, t + decay);
    modulator.connect(depth).connect(carrier.frequency);
    modulator.start(t);
    modulator.stop(t + decay + 0.05);
    this.envelope(ctx, carrier, PLING.gain, 0.003, decay, t, out);
  }

  private thump(ctx: AudioContext, t: number, p: { from: number; to: number; sweep: number; decay: number; gain: number }, detune: number, out: AudioNode | null): void {
    if (!out) return;
    const osc = ctx.createOscillator();
    osc.frequency.setValueAtTime(p.from * detune, t);
    osc.frequency.exponentialRampToValueAtTime(p.to * detune, t + p.sweep);
    this.envelope(ctx, osc, p.gain, 0.003, p.decay, t, out);
  }

  /** Comic "hup": a short rising triangle sweep. */
  private sweep(ctx: AudioContext, t: number, p: { from: number; to: number; sweep: number; decay: number; gain: number }): void {
    if (!this.sfx) return;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(p.from, t);
    osc.frequency.exponentialRampToValueAtTime(p.to, t + p.sweep);
    this.envelope(ctx, osc, p.gain, 0.008, p.decay, t, this.sfx);
  }

  /** Slide: band-passed noise sweeping down plus a soft slide whistle. */
  private swoosh(ctx: AudioContext, t: number): void {
    const out = this.sfx;
    if (!out) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(ctx, SLIDE.length);
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = SLIDE.q;
    band.frequency.setValueAtTime(SLIDE.from, t);
    band.frequency.exponentialRampToValueAtTime(SLIDE.to, t + SLIDE.length);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(SLIDE.gain, t + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + SLIDE.length);
    source.connect(band).connect(gain).connect(out);
    source.start(t);
    const whistle = ctx.createOscillator();
    whistle.frequency.setValueAtTime(SLIDE.whistleFrom, t);
    whistle.frequency.exponentialRampToValueAtTime(SLIDE.whistleTo, t + SLIDE.length);
    this.envelope(ctx, whistle, SLIDE.whistleGain, 0.03, SLIDE.length, t, out);
  }

  /** Falling: band-passed noise sweeping down over the whole fall. */
  private whoosh(ctx: AudioContext, t: number, p: { from: number; to: number; length: number; gain: number; q: number }): void {
    const out = this.sfx;
    if (!out) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(ctx, p.length);
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = p.q;
    band.frequency.setValueAtTime(p.from, t);
    band.frequency.exponentialRampToValueAtTime(p.to, t + p.length);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(p.gain, t + p.length * 0.6);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + p.length);
    source.connect(band).connect(gain).connect(out);
    source.start(t);
  }

  /** Wrong-answer buzzer: two detuned square waves sliding down through a low-pass. */
  private buzzer(ctx: AudioContext, t: number): void {
    const out = this.sfx;
    if (!out) return;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = QUIZ_WRONG.cutoff;
    lowpass.connect(out);
    for (const detune of [1, 1.03]) {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.setValueAtTime(QUIZ_WRONG.from * detune, t);
      osc.frequency.exponentialRampToValueAtTime(QUIZ_WRONG.to * detune, t + QUIZ_WRONG.length);
      this.envelope(ctx, osc, QUIZ_WRONG.gain, 0.01, QUIZ_WRONG.length, t, lowpass);
    }
  }

  /** High-passed noise with a fade-out (fizz). */
  private hiss(ctx: AudioContext, t: number, length: number, cutoff: number, peak: number): void {
    const out = this.sfx;
    if (!out) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(ctx, length);
    const highpass = ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = cutoff;
    const gain = ctx.createGain();
    gain.gain.value = peak;
    source.connect(highpass).connect(gain).connect(out);
    source.start(t);
  }

  /** "Ahh": a sawtooth voice falling in pitch through an "a" formant, with a breathy noise layer. */
  private ahh(ctx: AudioContext, t: number): void {
    const out = this.sfx;
    if (!out) return;
    const formant = ctx.createBiquadFilter();
    formant.type = 'bandpass';
    formant.frequency.value = AHH.formant;
    formant.Q.value = AHH.q;
    formant.connect(out);
    const voice = ctx.createOscillator();
    voice.type = 'sawtooth';
    voice.frequency.setValueAtTime(AHH.from, t);
    voice.frequency.exponentialRampToValueAtTime(AHH.to, t + AHH.length);
    this.envelope(ctx, voice, AHH.gain, 0.06, AHH.length, t, formant);
    this.noiseBurst(ctx, t, AHH.length, AHH.breathCutoff, AHH.breath, out);
  }

  /** Square-wave beeps (reversing alarm, turn signal), scaled by loudness. */
  private beeps(ctx: AudioContext, t: number, p: { frequency: number; beeps: number; length: number; gap: number; gain: number }, loudness: number): void {
    const out = this.sfx;
    if (!out) return;
    for (let i = 0; i < p.beeps; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = p.frequency;
      this.envelope(ctx, osc, p.gain * loudness, 0.005, p.length, t + i * (p.length + p.gap), out);
    }
  }

  /** Chain sling creaking under a swinging load: a rasping, slightly falling squeak, scaled by loudness. */
  private creak(ctx: AudioContext, t: number, loudness: number): void {
    const out = this.sfx;
    if (!out) return;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = CREAK.band;
    band.Q.value = CREAK.q;
    band.connect(out);
    const voice = ctx.createOscillator();
    voice.type = 'sawtooth';
    voice.frequency.setValueAtTime(CREAK.from, t);
    voice.frequency.exponentialRampToValueAtTime(CREAK.to, t + CREAK.length);
    // A fast wobble of the pitch makes the squeak rasp like steel links rubbing.
    const wobble = ctx.createOscillator();
    wobble.frequency.value = CREAK.wobbleHz;
    const depth = ctx.createGain();
    depth.gain.value = CREAK.wobbleDepth;
    wobble.connect(depth).connect(voice.frequency);
    wobble.start(t);
    wobble.stop(t + CREAK.attack + CREAK.length + 0.02);
    this.envelope(ctx, voice, CREAK.gain * loudness, CREAK.attack, CREAK.length, t, band);
  }

  /** Two-tone vehicle horn: detuned sawtooth chord through a low-pass. */
  private horn(ctx: AudioContext, t: number, loudness: number): void {
    const out = this.sfx;
    if (!out) return;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = HORN.cutoff;
    lowpass.connect(out);
    for (const note of HORN.notes) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = note;
      this.envelope(ctx, osc, HORN.gain * loudness, 0.02, HORN.length, t, lowpass);
    }
  }

  private noiseBurst(ctx: AudioContext, t: number, length: number, cutoff: number, peak: number, out: AudioNode | null): void {
    if (!out) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(ctx, length);
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = cutoff;
    const gain = ctx.createGain();
    gain.gain.value = peak;
    source.connect(lowpass).connect(gain).connect(out);
    source.start(t);
  }

  /** White noise with a linear fade-out, cached per length. */
  private noiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
    const cached = this.noise.get(seconds);
    if (cached) return cached;
    const length = Math.ceil(ctx.sampleRate * seconds);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
    this.noise.set(seconds, buffer);
    return buffer;
  }

  private envelope(ctx: AudioContext, source: OscillatorNode, peak: number, attack: number, decay: number, t: number, out: AudioNode): void {
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    source.connect(gain).connect(out);
    source.start(t);
    source.stop(t + attack + decay + 0.02);
  }
}
